/**
 * @fileoverview MergeDialog — modal dialog for the MasterGroupedView Merge action.
 *
 * Displays the selected PlaceId sub-groups as candidate locations. The user
 * chooses a destination (canonical) master venue via radio buttons. Each option
 * shows the venue name, address, and an embedded Google Maps iframe preview.
 * The user may also edit the destination `UnifiedName` before confirming —
 * the field is **required** and the Merge button is disabled while it is empty.
 */

import * as React from 'react';
import {
    Box,
    Button,
    FormControlLabel,
    Radio,
    RadioGroup,
    Skeleton,
    TextField,
    Typography,
} from '@mui/material';
import MergeIcon from '@mui/icons-material/MergeType';
import MDialog from '../../../components/controls/MDialog';
import { MasterVenueRecord } from '../utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * A PlaceId sub-group of master venue records, passed in from MasterGroupedView.
 * Only the fields consumed by MergeDialog are declared here.
 */
export interface MergeSubGroup {
    /** Google Places unique identifier; empty string for records without one. */
    placeId: string;
    /** The `MasterVenueId` for this sub-group (from the first record). */
    masterVenueId: number;
    /** Display name for this location. */
    googleUnifiedName: string;
    /** Formatted address. */
    formattedAddress: string;
    /** Google Maps URI (used for the iframe embed). */
    googleMapsUri: string;
    /** All master venue records in this sub-group. */
    records: MasterVenueRecord[];
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** Props for {@link MergeDialog}. */
export interface MergeDialogProps {
    /** Whether the dialog is open. */
    open: boolean;
    /** All PlaceId sub-groups selected for merging. */
    selectedSubGroups: MergeSubGroup[];
    /**
     * Called when the user confirms with the chosen destination MasterVenueId
     * and the (optionally edited) destination UnifiedName.
     */
    onConfirm: (destinationMasterVenueId: number, destinationUnifiedName: string) => void;
    /** Called when the dialog is cancelled. */
    onCancel: () => void;
}

// ---------------------------------------------------------------------------
// MapEmbed — inline Google Maps iframe for a formatted address
// ---------------------------------------------------------------------------

/** Props for {@link MapEmbed}. */
interface MapEmbedProps {
    /** Full formatted address to embed. */
    formattedAddress: string;
    /** Human-readable venue name used in the iframe title. */
    venueName: string;
}

/**
 * Renders a compact Google Maps iframe keyed by `formattedAddress`.
 * Shows a skeleton while the iframe loads.
 *
 * @param props.formattedAddress - Address used as the map search query.
 * @param props.venueName        - Venue name for the iframe accessibility title.
 */
const MapEmbed: React.FC<MapEmbedProps> = ({ formattedAddress, venueName }) => {
    const [loaded, setLoaded] = React.useState(false);
    const [iframeSrc, setIframeSrc] = React.useState('');

    // Set src dynamically after mount/address changes to ensure onLoad is registered first.
    React.useEffect(() => {
        setLoaded(false);
        if (formattedAddress) {
            const url = `https://maps.google.com/maps?q=${encodeURIComponent(formattedAddress)}&output=embed`;
            setIframeSrc(url);
        } else {
            setIframeSrc('');
        }
    }, [formattedAddress]);

    if (!formattedAddress) return null;

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                height: '180px',
                borderRadius: 1,
                overflow: 'hidden',
                border: 1,
                borderColor: 'divider',
                mt: 0.5,
            }}
        >
            {!loaded && (
                <Skeleton
                    variant="rectangular"
                    sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 1 }}
                />
            )}
            {iframeSrc && (
                <Box
                    component="iframe"
                    src={iframeSrc}
                    title={`Map preview for ${venueName}`}
                    allowFullScreen
                    // Removed loading="lazy" to prevent deferred loading bugs in dialog transitions.
                    referrerPolicy="no-referrer-when-downgrade"
                    onLoad={() => setLoaded(true)}
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        border: 0,
                        opacity: loaded ? 1 : 0,
                        transition: 'opacity 0.3s ease',
                    }}
                />
            )}
        </Box>
    );
};

// ---------------------------------------------------------------------------
// MergeDialog
// ---------------------------------------------------------------------------

/**
 * Dialog that displays the selected locations and lets the user choose a
 * destination master venue. Each option includes an embedded Google Maps
 * iframe preview. The user can also edit the destination `UnifiedName`
 * before confirming (defaults to the selected destination's current name).
 *
 * `Destination Unified Name` is **required** — the Merge button remains
 * disabled while the field is empty.
 *
 * @param props.open              - Whether the dialog is open.
 * @param props.selectedSubGroups - The locations selected for merging.
 * @param props.onConfirm         - Callback with the chosen destination id and edited name.
 * @param props.onCancel          - Cancel callback.
 */
const MergeDialog: React.FC<MergeDialogProps> = ({
    open,
    selectedSubGroups,
    onConfirm,
    onCancel,
}) => {
    const [destinationId, setDestinationId] = React.useState<string>('');
    const [editedUnifiedName, setEditedUnifiedName] = React.useState<string>('');
    const [loading, setLoading] = React.useState<boolean>(false);

    // Reset states when dialog opens.
    React.useEffect(() => {
        if (open && selectedSubGroups.length > 0) {
            const first = selectedSubGroups[0];
            setDestinationId(String(first.masterVenueId));
            setEditedUnifiedName(first.googleUnifiedName);
            setLoading(false); // Reset loading state when opening
        }
    }, [open, selectedSubGroups]);

    /** When the selected destination changes, update the editable name. */
    const handleDestinationChange = (_e: React.ChangeEvent<HTMLInputElement>, val: string): void => {
        setDestinationId(val);
        const sg = selectedSubGroups.find(s => String(s.masterVenueId) === val);
        setEditedUnifiedName(sg?.googleUnifiedName ?? '');
    };

    const handleConfirm = (): void => {
        const id = parseInt(destinationId, 10);
        if (!isNaN(id) && id > 0 && editedUnifiedName.trim()) {
            onConfirm(id, editedUnifiedName.trim());
            setLoading(true);
        }
    };

    const canConfirm = !!destinationId && editedUnifiedName.trim().length > 0;

    return (
        <MDialog
            open={open}
            title={'Merge Locations'}
            handleClose={onCancel}
            maxWidth="sm"
            disabled={loading}
            action={
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<MergeIcon />}
                    onClick={handleConfirm}
                    disabled={!canConfirm}
                >
                    Merge
                </Button>
            }
        >
            <Typography variant="body2" gutterBottom>
                    Select the <strong>destination</strong> (canonical location) to merge into.
                    All other selected locations will be redirected to the destination.
            </Typography>

            <RadioGroup sx={{ width: '100%' }} value={destinationId} onChange={handleDestinationChange}>
                {selectedSubGroups.map(sg => (
                    <FormControlLabel
                        key={sg.placeId || `__no_place_id_${sg.masterVenueId}`}
                        value={String(sg.masterVenueId)}
                        control={<Radio size="small" />}
                        label={
                            <Box sx={{ py: 0.5, width: '100%' }}>
                                <Typography variant="body2" fontWeight={600}>
                                    {sg.googleUnifiedName || '(unnamed)'}
                                </Typography>
                                {sg.formattedAddress && (
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {sg.formattedAddress}
                                    </Typography>
                                )}
                                {/* Embedded map preview instead of external link */}
                                {sg.formattedAddress && (
                                    <MapEmbed
                                        formattedAddress={sg.formattedAddress}
                                        venueName={sg.googleUnifiedName}
                                    />
                                )}
                            </Box>
                        }
                        sx={{ alignItems: 'flex-start', mb: 1.5,
                            width: '100%',
                            mx: 0,
                            '& .MuiFormControlLabel-label': {
                                width: '100%',
                            },
                        }}
                    />
                ))}
            </RadioGroup>

            {/* Destination Unified Name — required */}
            <Box>
                <TextField
                    label="Destination Unified Name"
                    size="small"
                    fullWidth
                    required
                    value={editedUnifiedName}
                    onChange={e => setEditedUnifiedName(e.target.value)}
                    placeholder="Enter destination unified name"
                    error={!editedUnifiedName.trim()}
                    helperText={
                        !editedUnifiedName.trim()
                            ? 'Unified Name is required.'
                            : ' '
                    }
                    inputProps={{
                        'aria-label': 'destination unified name',
                    }}
                />
            </Box>
        </MDialog>
    );
};

export default MergeDialog;